(() => {
  const initSiteHeader = (header) => {
    if (!header || header.dataset.cgHeaderInitialized === 'true') {
      return;
    }

    header.dataset.cgHeaderInitialized = 'true';

    const menuToggle = header.querySelector('.cg-site-header__menu-toggle');
    const menu = header.querySelector(`#${menuToggle?.getAttribute('aria-controls') || ''}`);

    if (!menuToggle || !menu) {
      return;
    }

    const setMenuOpen = (isOpen) => {
      menu.hidden = !isOpen;
      menuToggle.setAttribute('aria-expanded', String(isOpen));
    };

    menuToggle.addEventListener('click', () => {
      setMenuOpen(menu.hidden);
    });

    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) {
        setMenuOpen(false);
      }
    });

    document.addEventListener('click', (event) => {
      if (!menu.hidden && !header.contains(event.target)) {
        setMenuOpen(false);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    });
  };

  const initProductMain = (section) => {
    if (!section || section.dataset.cgProductInitialized === 'true') {
      return;
    }

    section.dataset.cgProductInitialized = 'true';

    const form = section.querySelector('.cg-product-form');
    const variantInput = form?.querySelector('[name="id"]') || section.querySelector('[data-cg-variant-id]');
    const atcButton = section.querySelector('[data-cg-product-atc]');
    const priceNode = section.querySelector('[data-cg-product-price]');
    const comparePriceNode = section.querySelector('[data-cg-product-compare-price]');
    const stickyButton = section.querySelector('[data-cg-sticky-atc-button]');
    const stickyPriceNode = section.querySelector('[data-cg-sticky-price]');
    const variantsScript = section.querySelector('[data-cg-product-variants]');
    const quantityInput = section.querySelector('[data-cg-quantity-stepper] [name="quantity"]');
    const productInformation = section.querySelector('[data-cg-product-information]');
    const productInformationMessage = productInformation?.querySelector('[data-cg-product-information-message]');
    const optionGroups = Array.from(section.querySelectorAll('[data-cg-option-group]'));
    const mediaStage = section.querySelector('[data-cg-media-stage]');
    const mediaItems = Array.from(section.querySelectorAll('[data-cg-media-id]'));
    const thumbs = Array.from(section.querySelectorAll('[data-cg-media-thumb]'));
    const mediaDots = Array.from(section.querySelectorAll('[data-cg-media-dot]'));
    const mediaOpenItems = Array.from(section.querySelectorAll('[data-cg-media-open]'));
    const lightbox = section.querySelector('[data-cg-media-lightbox]');
    const lightboxDialog = section.querySelector('[data-cg-media-lightbox-dialog]');
    const lightboxItems = Array.from(section.querySelectorAll('[data-cg-lightbox-media-id]'));
    const lightboxClose = section.querySelector('[data-cg-media-lightbox-close]');
    const lightboxPrev = section.querySelector('[data-cg-media-lightbox-prev]');
    const lightboxNext = section.querySelector('[data-cg-media-lightbox-next]');
    let lightboxLastFocus = null;
    let activeLightboxMediaId =
      lightboxItems.find((item) => item.classList.contains('is-active'))?.dataset.cgLightboxMediaId ||
      lightboxItems[0]?.dataset.cgLightboxMediaId ||
      null;
    let suppressNextMediaClick = false;
    let suppressNextMediaClickTimer = null;

    let variants = [];
    if (variantsScript?.textContent) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        variants = [];
      }
    }

    let cartSubtotal = Number(productInformation?.dataset.cartSubtotal) || 0;

    const getProductInformationVariant = () => {
      const activeVariantId = variantInput?.value;
      return variants.find((variant) => String(variant.id) === String(activeVariantId));
    };

    const getProductInformationQuantity = () => {
      const value = Number(quantityInput?.value);
      return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
    };

    const updateProductInformation = () => {
      if (!productInformation) {
        return;
      }

      const threshold = Number(productInformation.dataset.thresholdCents) || 2500;
      const flatRateMessage =
        productInformation.dataset.flatRateMessage || '$7.95 flat rate shipping or free shipping at $25+';
      const freeShippingMessage = productInformation.dataset.freeShippingMessage || 'Free shipping.';
      const variant = getProductInformationVariant();
      const selectionTotal = Math.max(0, Number(variant?.price) || 0) * getProductInformationQuantity();
      const projectedSubtotal = cartSubtotal + selectionTotal;
      const qualifiesForFreeShipping = projectedSubtotal >= threshold;
      const state = qualifiesForFreeShipping ? 'free-shipping' : 'flat-rate';
      const message = qualifiesForFreeShipping ? freeShippingMessage : flatRateMessage;

      productInformation.dataset.state = state;
      if (productInformationMessage && productInformationMessage.textContent.trim() !== message) {
        productInformationMessage.textContent = message;
      }
    };

    const refreshProductInformationCart = () => {
      if (!productInformation || typeof window.fetch !== 'function') {
        return;
      }

      const routesRoot = window.Shopify?.routes?.root || '/';
      const cartUrl = `${routesRoot.endsWith('/') ? routesRoot : `${routesRoot}/`}cart.js`;

      window
        .fetch(cartUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Cart request failed with ${response.status}`);
          }
          return response.json();
        })
        .then((cartData) => {
          const nextSubtotal = Number(cartData.items_subtotal_price ?? cartData.total_price);
          if (Number.isFinite(nextSubtotal)) {
            cartSubtotal = nextSubtotal;
          }
          updateProductInformation();
        })
        .catch(() => {
          updateProductInformation();
        });
    };

    const setLightboxMedia = (mediaId) => {
      if (!lightboxItems.length) {
        return;
      }

      const requestedId = String(mediaId || '');
      const targetId = lightboxItems.some((item) => item.dataset.cgLightboxMediaId === requestedId)
        ? requestedId
        : lightboxItems[0].dataset.cgLightboxMediaId;

      activeLightboxMediaId = targetId;
      lightboxItems.forEach((item) => {
        item.classList.toggle('is-active', item.dataset.cgLightboxMediaId === targetId);
      });
    };

    const closeLightbox = () => {
      if (!lightbox || lightbox.hidden) {
        return;
      }

      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cg-product-media-lightbox-open');
      document.body?.classList.remove('cg-product-media-lightbox-open');
      document.removeEventListener('keydown', handleLightboxKeydown);

      if (lightboxLastFocus && typeof lightboxLastFocus.focus === 'function') {
        lightboxLastFocus.focus({ preventScroll: true });
      }
    };

    const openLightbox = (mediaId, trigger) => {
      if (!lightbox || !lightboxItems.length) {
        return;
      }

      lightboxLastFocus = trigger || document.activeElement;
      setLightboxMedia(mediaId);
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      lightbox.classList.toggle('is-single-image', lightboxItems.length < 2);
      document.documentElement.classList.add('cg-product-media-lightbox-open');
      document.body?.classList.add('cg-product-media-lightbox-open');
      document.addEventListener('keydown', handleLightboxKeydown);

      window.requestAnimationFrame(() => {
        (lightboxDialog || lightboxClose || lightbox).focus({ preventScroll: true });
      });
    };

    const playActiveMediaVideo = (item) => {
      const video = item?.querySelector('video');
      if (!video) {
        return;
      }

      const playback = video.play();
      if (!playback || typeof playback.catch !== 'function') {
        return;
      }

      playback.catch(() => {
        if (!item.classList.contains('is-active') || !video.paused) {
          return;
        }

        video.muted = true;
        const mutedPlayback = video.play();
        if (mutedPlayback && typeof mutedPlayback.catch === 'function') {
          mutedPlayback.catch(() => {});
        }
      });
    };

    const activateMedia = (mediaId) => {
      if (!mediaId) {
        return;
      }

      const targetId = String(mediaId);
      let activeMediaItem = null;

      mediaItems.forEach((item) => {
        const isActive = item.dataset.cgMediaId === targetId;
        item.classList.toggle('is-active', isActive);

        if (isActive) {
          activeMediaItem = item;
        } else {
          item.querySelector('video')?.pause();
        }

        if (item.hasAttribute('data-cg-media-open')) {
          item.tabIndex = isActive ? 0 : -1;
        }
      });

      thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.cgMediaThumb === targetId;
        thumb.classList.toggle('is-active', isActive);
        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      mediaDots.forEach((dot) => {
        const isActive = dot.dataset.cgMediaDot === targetId;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      setLightboxMedia(targetId);
      playActiveMediaVideo(activeMediaItem);
    };

    const getActiveMediaIndex = () => {
      const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
      return activeIndex >= 0 ? activeIndex : 0;
    };

    const activateMediaByIndex = (index) => {
      if (!mediaItems.length) {
        return;
      }

      const nextIndex = (index + mediaItems.length) % mediaItems.length;
      activateMedia(mediaItems[nextIndex]?.dataset.cgMediaId);
    };

    const getActiveLightboxIndex = () => {
      const activeIndex = lightboxItems.findIndex(
        (item) => item.dataset.cgLightboxMediaId === activeLightboxMediaId
      );
      return activeIndex >= 0 ? activeIndex : 0;
    };

    const activateLightboxByDirection = (direction) => {
      if (!lightboxItems.length) {
        return;
      }

      const nextIndex = (getActiveLightboxIndex() + direction + lightboxItems.length) % lightboxItems.length;
      activateMedia(lightboxItems[nextIndex]?.dataset.cgLightboxMediaId);
    };

    function handleLightboxKeydown(event) {
      if (!lightbox || lightbox.hidden) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        activateLightboxByDirection(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        activateLightboxByDirection(1);
      }
    }

    const suppressMediaClickOnce = () => {
      suppressNextMediaClick = true;

      if (suppressNextMediaClickTimer) {
        window.clearTimeout(suppressNextMediaClickTimer);
      }

      suppressNextMediaClickTimer = window.setTimeout(() => {
        suppressNextMediaClick = false;
        suppressNextMediaClickTimer = null;
      }, 320);
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => activateMedia(thumb.dataset.cgMediaThumb));
    });

    mediaDots.forEach((dot) => {
      dot.addEventListener('click', () => activateMedia(dot.dataset.cgMediaDot));
    });

    mediaOpenItems.forEach((item) => {
      item.addEventListener('click', (event) => {
        if (suppressNextMediaClick) {
          event.preventDefault();
          suppressNextMediaClick = false;
          return;
        }

        openLightbox(item.dataset.cgMediaOpen, item);
      });

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        event.preventDefault();
        openLightbox(item.dataset.cgMediaOpen, item);
      });
    });

    if (lightbox) {
      lightbox.classList.toggle('is-single-image', lightboxItems.length < 2);

      lightboxClose?.addEventListener('click', closeLightbox);
      lightboxPrev?.addEventListener('click', () => activateLightboxByDirection(-1));
      lightboxNext?.addEventListener('click', () => activateLightboxByDirection(1));

      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) {
          closeLightbox();
        }
      });
    }

    if (mediaStage && mediaItems.length > 1) {
      let swipeState = null;
      let swipeResetTimer = null;
      const swipePreviewDistance = 220;
      const swipeSettleMs = 180;

      const clearSwipeResetTimer = () => {
        if (swipeResetTimer) {
          window.clearTimeout(swipeResetTimer);
          swipeResetTimer = null;
        }
      };

      const clearSwipeVisuals = () => {
        clearSwipeResetTimer();
        mediaStage.classList.remove('is-swipe-previewing', 'is-swipe-settling');
        mediaStage.style.removeProperty('--cg-product-swipe-current-opacity');
        mediaStage.style.removeProperty('--cg-product-swipe-next-opacity');
        mediaItems.forEach((item) => item.classList.remove('is-swipe-preview'));
      };

      const applySwipeVisuals = (direction, progress, isSettling = false) => {
        const activeIndex = getActiveMediaIndex();
        const previewIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
        const previewItem = mediaItems[previewIndex];
        const easedProgress = Math.max(0, Math.min(progress, 1));

        mediaItems.forEach((item) => {
          item.classList.toggle('is-swipe-preview', item === previewItem);
        });

        mediaStage.classList.add('is-swipe-previewing');
        mediaStage.classList.toggle('is-swipe-settling', isSettling);
        mediaStage.style.setProperty('--cg-product-swipe-current-opacity', String(1 - easedProgress));
        mediaStage.style.setProperty('--cg-product-swipe-next-opacity', String(easedProgress));
      };

      const resetSwipe = () => {
        swipeState = null;
      };

      mediaStage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
          return;
        }

        if (event.target.closest('a, button, input, select, textarea, summary')) {
          return;
        }

        clearSwipeVisuals();

        swipeState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          isHorizontal: false,
          direction: 1
        };

        try {
          mediaStage.setPointerCapture(event.pointerId);
        } catch (error) {}
      });

      mediaStage.addEventListener(
        'pointermove',
        (event) => {
          if (!swipeState || event.pointerId !== swipeState.pointerId) {
            return;
          }

          const deltaX = event.clientX - swipeState.startX;
          const deltaY = event.clientY - swipeState.startY;

          swipeState.lastX = event.clientX;
          swipeState.lastY = event.clientY;

          if (
            !swipeState.isHorizontal &&
            Math.abs(deltaX) > 10 &&
            Math.abs(deltaX) > Math.abs(deltaY) * 1.25
          ) {
            swipeState.isHorizontal = true;
          }

          if (swipeState.isHorizontal) {
            swipeState.direction = deltaX < 0 ? 1 : -1;
            applySwipeVisuals(
              swipeState.direction,
              Math.min(Math.abs(deltaX) / swipePreviewDistance, 1)
            );
            event.preventDefault();
          }
        },
        { passive: false }
      );

      const finishSwipe = (event, shouldAnimateBack = true) => {
        if (!swipeState || event.pointerId !== swipeState.pointerId) {
          return;
        }

        const deltaX = swipeState.lastX - swipeState.startX;
        const deltaY = swipeState.lastY - swipeState.startY;
        const direction = deltaX === 0 ? swipeState.direction : deltaX < 0 ? 1 : -1;
        const isSwipe = Math.abs(deltaX) >= 44 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3;

        if (!swipeState.isHorizontal) {
          clearSwipeVisuals();
          resetSwipe();
          return;
        }

        suppressMediaClickOnce();
        applySwipeVisuals(direction, isSwipe ? 1 : 0, shouldAnimateBack);

        swipeResetTimer = window.setTimeout(() => {
          if (isSwipe) {
            activateMediaByIndex(getActiveMediaIndex() + direction);
          }

          clearSwipeVisuals();
          resetSwipe();
        }, shouldAnimateBack ? swipeSettleMs : 0);
      };

      mediaStage.addEventListener('pointerup', (event) => {
        finishSwipe(event);
      });

      mediaStage.addEventListener('pointercancel', (event) => {
        finishSwipe(event);
      });
    }

    const getSelectedOptions = () => {
      return optionGroups.map((group) => {
        const checked = group.querySelector('[data-cg-option-input]:checked');
        return checked ? checked.value : null;
      });
    };

    const findSelectedVariant = () => {
      if (!variants.length) {
        return null;
      }

      if (!optionGroups.length) {
        return variants[0];
      }

      const selectedOptions = getSelectedOptions();
      return variants.find((variant) => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });
    };

    const setButtonState = (button, variant) => {
      if (!button) {
        return;
      }

      const availableText = button.dataset.availableText || atcButton?.dataset.availableText || 'Add to cart';
      const soldOutText = button.dataset.soldOutText || atcButton?.dataset.soldOutText || 'Sold out';
      const unavailableText = button.dataset.unavailableText || atcButton?.dataset.unavailableText || 'Unavailable';
      const isAvailable = Boolean(variant?.available);

      if (isPrelaunchInterceptionEnabled() && button.matches('[data-cg-prelaunch-open]')) {
        button.disabled = false;
        button.removeAttribute('disabled');
        button.setAttribute('aria-disabled', 'false');
        button.textContent = button.dataset.cgPrelaunchLabel || 'Join the drop';
        return;
      }

      button.disabled = !isAvailable;
      button.setAttribute('aria-disabled', String(!isAvailable));
      button.textContent = variant ? (isAvailable ? availableText : soldOutText) : unavailableText;
    };

    const updateProductState = () => {
      const variant = findSelectedVariant();

      if (variantInput && variant?.id) {
        variantInput.value = variant.id;
      }

      if (priceNode) {
        priceNode.textContent = variant?.price_formatted || '';
      }

      if (stickyPriceNode) {
        stickyPriceNode.textContent = variant?.price_formatted || '';
      }

      if (comparePriceNode) {
        const showCompare = Boolean(
          variant &&
            variant.compare_at_price &&
            Number(variant.compare_at_price) > Number(variant.price)
        );
        comparePriceNode.hidden = !showCompare;
        comparePriceNode.textContent = showCompare ? variant.compare_at_price_formatted : '';
      }

      setButtonState(atcButton, variant);
      setButtonState(stickyButton, variant);

      if (variant?.featured_media_id) {
        activateMedia(variant.featured_media_id);
      }

      updateProductInformation();
    };

    optionGroups.forEach((group) => {
      group.addEventListener('change', updateProductState);
    });

    section.querySelectorAll('[data-cg-quantity-stepper]').forEach((stepper) => {
      const input = stepper.querySelector('input');
      const decrease = stepper.querySelector('[data-cg-quantity-decrease]');
      const increase = stepper.querySelector('[data-cg-quantity-increase]');

      if (!input || !decrease || !increase) {
        return;
      }

      const changeQuantity = (amount) => {
        const min = Number(input.min) || 1;
        const current = Number(input.value) || min;
        input.value = String(Math.max(min, current + amount));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      decrease.addEventListener('click', () => changeQuantity(-1));
      increase.addEventListener('click', () => changeQuantity(1));
      input.addEventListener('change', () => {
        const min = Number(input.min) || 1;
        const current = Number(input.value) || min;
        input.value = String(Math.max(min, current));
      });
    });

    quantityInput?.addEventListener('input', updateProductInformation);
    quantityInput?.addEventListener('change', updateProductInformation);
    variantInput?.addEventListener('change', updateProductInformation);

    if (stickyButton && form && atcButton) {
      stickyButton.addEventListener('click', () => {
        if (stickyButton.disabled) {
          return;
        }

        if (
          stickyButton.matches('[data-cg-prelaunch-open]') &&
          isPrelaunchInterceptionEnabled()
        ) {
          return;
        }

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(atcButton);
        } else {
          atcButton.click();
        }
      });
    }

    const stickyBar = section.querySelector('[data-cg-sticky-atc]');
    if (stickyBar) {
      const productArea = section.querySelector('.cg-product-hero__inner') || section;
      const stickyTrigger = document.querySelector('.cg-image-board') || productArea;
      const footer = document.querySelector('footer, .cg-site-footer');
      let isProductAreaBlockingSticky = true;
      let isFooterVisible = false;
      let hasReachedStickyTrigger = false;
      let stickyBarFrame = null;

      const updateStickyBarVisibility = () => {
        const shouldShow = hasReachedStickyTrigger && !isProductAreaBlockingSticky && !isFooterVisible;
        stickyBar.classList.toggle('is-sticky-visible', shouldShow);
        stickyBar.classList.toggle('is-product-area-visible', isProductAreaBlockingSticky);
        stickyBar.classList.toggle('is-before-sticky-trigger', !hasReachedStickyTrigger);
        stickyBar.classList.toggle('is-footer-hidden', isFooterVisible);

        if (shouldShow) {
          stickyBar.style.display = 'grid';
          stickyBar.style.opacity = '1';
          stickyBar.style.pointerEvents = 'auto';
          stickyBar.style.transform = 'translate(var(--cg-sticky-atc-x, 0px), 0)';
          stickyBar.style.visibility = 'visible';
        } else {
          stickyBar.style.display = 'grid';
          stickyBar.style.opacity = '0';
          stickyBar.style.pointerEvents = 'none';
          stickyBar.style.transform = 'translate(var(--cg-sticky-atc-x, 0px), calc(100% + 18px))';
          stickyBar.style.visibility = 'hidden';
        }
      };

      const elementIntersectsViewport = (element) => {
        if (!element) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight;
      };

      const elementOverlapsStickyZone = (element) => {
        if (!element) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        const stickyHeight = stickyBar.offsetHeight || 88;
        const stickySafeTop = window.innerHeight - stickyHeight - 24;
        return rect.bottom > stickySafeTop && rect.top < window.innerHeight;
      };

      const elementHasReachedRevealPoint = (element) => {
        if (!element) {
          return true;
        }

        const rect = element.getBoundingClientRect();
        const revealPoint = window.innerHeight + 120;
        return rect.top <= revealPoint;
      };

      const syncStickyBarFromLayout = () => {
        isProductAreaBlockingSticky = elementOverlapsStickyZone(productArea);
        isFooterVisible = elementIntersectsViewport(footer);
        hasReachedStickyTrigger = elementHasReachedRevealPoint(stickyTrigger);
        updateStickyBarVisibility();
      };

      const requestStickyBarSync = () => {
        if (stickyBarFrame) {
          return;
        }

        stickyBarFrame = window.requestAnimationFrame(() => {
          stickyBarFrame = null;
          syncStickyBarFromLayout();
        });
      };

      syncStickyBarFromLayout();
      window.addEventListener('scroll', requestStickyBarSync, { passive: true });
      window.addEventListener('resize', requestStickyBarSync);
    }

    updateProductState();
    refreshProductInformationCart();
  };

  const normalizeProductTikTokEmbeds = (section) => {
    section
      .querySelectorAll('.cg-product-videos__embed blockquote.tiktok-embed[data-video-id]')
      .forEach((embed) => {
        const videoId = embed.dataset.videoId?.trim();

        if (!videoId || !/^\d+$/.test(videoId)) {
          return;
        }

        const player = document.createElement('iframe');
        const params = new URLSearchParams({
          controls: '1',
          fullscreen_button: '1',
          play_button: '1',
          progress_bar: '1',
          timestamp: '1',
          volume_control: '1'
        });

        player.className = 'cg-product-videos__tiktok-player';
        player.src = `https://www.tiktok.com/player/v1/${encodeURIComponent(videoId)}?${params}`;
        player.title = 'TikTok video player';
        player.loading = 'lazy';
        player.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        player.setAttribute('allowfullscreen', '');

        embed.replaceWith(player);
      });

    section
      .querySelectorAll('.cg-product-videos__embed script[src*="tiktok.com/embed.js"]')
      .forEach((script) => script.remove());
  };

  const activateProductVideoMedia = (section) => {
    if (!section || section.dataset.cgProductVideosMediaLoaded === 'true') {
      return;
    }

    section.dataset.cgProductVideosMediaLoaded = 'true';

    const loadedScriptSources = new Set(
      Array.from(document.scripts)
        .map((script) => script.src)
        .filter(Boolean)
    );

    section.querySelectorAll('[data-cg-product-video-template]').forEach((template) => {
      const shell = template.closest('[data-cg-product-video-shell]');
      const media = template.content.cloneNode(true);

      media.querySelectorAll('script').forEach((script) => {
        const source = script.getAttribute('src');
        let resolvedSource = '';

        if (source) {
          try {
            resolvedSource = new URL(source, document.baseURI).href;
          } catch (error) {}
        }

        if (resolvedSource && loadedScriptSources.has(resolvedSource)) {
          script.remove();
          return;
        }

        const activeScript = document.createElement('script');
        Array.from(script.attributes).forEach((attribute) => {
          activeScript.setAttribute(attribute.name, attribute.value);
        });
        activeScript.textContent = script.textContent;
        script.replaceWith(activeScript);

        if (resolvedSource) {
          loadedScriptSources.add(resolvedSource);
        }
      });

      template.replaceWith(media);
      shell?.setAttribute('aria-busy', 'false');
      shell?.setAttribute('data-cg-product-video-loaded', 'true');
    });

    normalizeProductTikTokEmbeds(section);

    window.requestAnimationFrame(() => {
      applyVideoEmbedSizing(section);
      window.instgrm?.Embeds?.process?.();
      window.twttr?.widgets?.load?.(section);
    });
  };

  const observeProductVideoMedia = (section) => {
    if (!section || section.dataset.cgProductVideosMediaObserved === 'true') {
      return;
    }

    section.dataset.cgProductVideosMediaObserved = 'true';

    if (!('IntersectionObserver' in window)) {
      activateProductVideoMedia(section);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        observer.disconnect();
        activateProductVideoMedia(section);
      },
      {
        rootMargin: '0px',
        threshold: 0.01
      }
    );

    observer.observe(section);
  };

  const initProductVideos = (section) => {
    if (!section || section.dataset.cgProductVideosInitialized === 'true') {
      return;
    }

    section.dataset.cgProductVideosInitialized = 'true';
    observeProductVideoMedia(section);

    const viewport = section.querySelector('[data-cg-product-videos-viewport]');
    const track = section.querySelector('[data-cg-product-videos-track]');
    const slides = Array.from(section.querySelectorAll('[data-cg-product-videos-slide]'));
    const previousButton = section.querySelector('[data-cg-product-videos-previous]');
    const nextButton = section.querySelector('[data-cg-product-videos-next]');
    const currentPosition = section.querySelector('[data-cg-product-videos-current]');

    if (!viewport || !track || !slides.length) {
      return;
    }

    let activeIndex = 0;
    let scrollFrame = null;

    const getClosestSlideIndex = () => {
      const trackLeft = track.offsetLeft;

      return slides.reduce((closestIndex, slide, index) => {
        const closestDistance = Math.abs(
          slides[closestIndex].offsetLeft - trackLeft - viewport.scrollLeft
        );
        const slideDistance = Math.abs(slide.offsetLeft - trackLeft - viewport.scrollLeft);

        return slideDistance < closestDistance ? index : closestIndex;
      }, 0);
    };

    const updateState = () => {
      activeIndex = getClosestSlideIndex();

      if (currentPosition) {
        currentPosition.textContent = String(activeIndex + 1);
      }

      if (previousButton) {
        previousButton.disabled = activeIndex <= 0;
      }

      if (nextButton) {
        nextButton.disabled = activeIndex >= slides.length - 1;
      }

      slides.forEach((slide, index) => {
        if (index === activeIndex) {
          slide.setAttribute('aria-current', 'true');
        } else {
          slide.removeAttribute('aria-current');
        }
      });
    };

    const scrollToSlide = (index) => {
      const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
      const left = slides[nextIndex].offsetLeft - track.offsetLeft;

      viewport.scrollTo({
        left,
        behavior: 'smooth'
      });
    };

    previousButton?.addEventListener('click', () => {
      scrollToSlide(activeIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      scrollToSlide(activeIndex + 1);
    });

    viewport.addEventListener(
      'scroll',
      () => {
        if (scrollFrame) {
          return;
        }

        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = null;
          updateState();
        });
      },
      { passive: true }
    );

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollToSlide(activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollToSlide(activeIndex + 1);
      }
    });

    window.addEventListener('resize', updateState);
    window.requestAnimationFrame(updateState);
  };

  const initProductRelated = (section) => {
    if (!section || section.dataset.cgRelatedInitialized === 'true') {
      return;
    }

    section.dataset.cgRelatedInitialized = 'true';

    const url = section.dataset.cgRecommendationsUrl;
    const grid = section.querySelector('[data-cg-product-related-grid]');

    if (!url || !grid) {
      return;
    }

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        const incomingGrid = html.querySelector('[data-cg-product-related-grid]');
        if (!incomingGrid || !incomingGrid.innerHTML.trim()) {
          return;
        }

        grid.innerHTML = incomingGrid.innerHTML;
      })
      .catch(() => {});
  };

  const getPrelaunchModal = () => document.querySelector('[data-cg-prelaunch-modal]');

  const isPrelaunchInterceptionEnabled = () => {
    const modal = getPrelaunchModal();
    return modal?.dataset.cgCtaInterception === 'true';
  };

  const scrollToPrelaunchFallback = (modal) => {
    const fallbackSelector = modal?.dataset.cgFallbackTarget || '.cg-email-signup';
    const fallback = document.querySelector(fallbackSelector);

    if (fallback) {
      fallback.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const initPrelaunchModal = (modal) => {
    if (!modal || modal.dataset.cgPrelaunchInitialized === 'true') {
      return;
    }

    modal.dataset.cgPrelaunchInitialized = 'true';

    const dialog = modal.querySelector('.cg-prelaunch-modal__dialog');
    const closeControls = Array.from(modal.querySelectorAll('[data-cg-prelaunch-close]'));
    let returnFocusTo = null;
    let suppressClicksUntil = 0;

    const consumeEvent = (event) => {
      if (!event) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    };

    const getFocusable = () => {
      return Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);
    };

    const closeModal = (event) => {
      consumeEvent(event);
      suppressClicksUntil = Date.now() + 600;
      modal.hidden = true;
      document.documentElement.classList.remove('cg-prelaunch-modal-open');

      if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
        returnFocusTo.focus({ preventScroll: true });
      }

      returnFocusTo = null;
    };

    const openModal = (trigger) => {
      if (modal.dataset.cgHasEmbed !== 'true') {
        scrollToPrelaunchFallback(modal);
        return;
      }

      returnFocusTo = trigger || document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('cg-prelaunch-modal-open');

      window.requestAnimationFrame(() => {
        const focusTarget = getFocusable()[0] || dialog;
        focusTarget?.focus({ preventScroll: true });
      });
    };

    modal.cgOpenPrelaunchModal = openModal;
    modal.cgClosePrelaunchModal = closeModal;

    if (!modal.hidden) {
      document.documentElement.classList.add('cg-prelaunch-modal-open');

      window.requestAnimationFrame(() => {
        const focusTarget = getFocusable()[0] || dialog;
        focusTarget?.focus({ preventScroll: true });
      });
    }

    closeControls.forEach((control) => {
      control.addEventListener('pointerdown', consumeEvent);
      control.addEventListener('pointerup', closeModal);
      control.addEventListener('touchstart', consumeEvent, { passive: false });
      control.addEventListener('click', closeModal);
      control.addEventListener('touchend', closeModal, { passive: false });
    });

    modal.addEventListener(
      'click',
      (event) => {
        if (Date.now() < suppressClicksUntil) {
          consumeEvent(event);
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (Date.now() < suppressClicksUntil) {
          consumeEvent(event);
        }
      },
      true
    );

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeModal();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initPrelaunchTriggers = () => {
    if (document.documentElement.dataset.cgPrelaunchTriggersInitialized === 'true') {
      return;
    }

    document.documentElement.dataset.cgPrelaunchTriggersInitialized = 'true';

    const applyPrelaunchTriggerState = () => {
      const enabled = isPrelaunchInterceptionEnabled();

      document.documentElement.classList.toggle('cg-prelaunch-active', enabled);
      document.documentElement.dataset.cgPrelaunchActive = String(enabled);

      if (enabled) {
        document.querySelectorAll('[data-cg-site-header]').forEach((header) => {
          const menuToggle = header.querySelector('.cg-site-header__menu-toggle');
          const menu = header.querySelector(`#${menuToggle?.getAttribute('aria-controls') || ''}`);

          if (menuToggle) {
            menuToggle.setAttribute('aria-expanded', 'false');
          }

          if (menu) {
            menu.hidden = true;
          }
        });
      }

      document.querySelectorAll('[data-cg-prelaunch-label]').forEach((trigger) => {
        if (!trigger.dataset.cgPrelaunchOriginalLabel) {
          trigger.dataset.cgPrelaunchOriginalLabel = trigger.textContent.trim();
        }

        const nextLabel = enabled
          ? trigger.dataset.cgPrelaunchLabel
          : trigger.dataset.cgPrelaunchOriginalLabel;

        if (nextLabel) {
          trigger.textContent = nextLabel;
        }
      });

      document.querySelectorAll('[data-cg-prelaunch-sold-out]').forEach((trigger) => {
        if (enabled) {
          if ('disabled' in trigger) {
            trigger.disabled = false;
          }

          trigger.removeAttribute('disabled');
          trigger.setAttribute('aria-disabled', 'false');
          return;
        }

        if ('disabled' in trigger) {
          trigger.disabled = true;
        }

        trigger.setAttribute('disabled', 'disabled');
        trigger.setAttribute('aria-disabled', 'true');
      });
    };

    applyPrelaunchTriggerState();
    document.addEventListener('shopify:section:load', applyPrelaunchTriggerState);

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cg-prelaunch-open], a[href="#cg-prelaunch-modal"]');
      const modal = getPrelaunchModal();

      if (!trigger || !isPrelaunchInterceptionEnabled()) {
        return;
      }

      event.preventDefault();

      if (modal?.cgOpenPrelaunchModal) {
        modal.cgOpenPrelaunchModal(trigger);
      } else {
        scrollToPrelaunchFallback(modal);
      }
    });

    document.addEventListener('submit', (event) => {
      if (!isPrelaunchInterceptionEnabled()) {
        return;
      }

      const submitter = event.submitter;
      const trigger = submitter?.closest?.('[data-cg-prelaunch-open]') || event.target.querySelector('[data-cg-prelaunch-open]');

      if (!trigger) {
        return;
      }

      event.preventDefault();

      const modal = getPrelaunchModal();
      if (modal?.cgOpenPrelaunchModal) {
        modal.cgOpenPrelaunchModal(trigger);
      } else {
        scrollToPrelaunchFallback(modal);
      }
    });
  };

  const shouldOpenPrelaunchFromUrl = () => {
    const params = new URLSearchParams(window.location.search);

    return (
      window.location.hash === '#cg-prelaunch-modal' ||
      window.location.hash === '#join-the-drop' ||
      params.has('join-the-drop') ||
      params.get('join') === 'drop' ||
      params.get('cg_prelaunch') === '1'
    );
  };

  const openPrelaunchFromUrl = () => {
    if (!shouldOpenPrelaunchFromUrl()) {
      return;
    }

    const modal = getPrelaunchModal();

    if (!modal) {
      return;
    }

    window.setTimeout(() => {
      if (modal.cgOpenPrelaunchModal) {
        modal.cgOpenPrelaunchModal();
      } else {
        scrollToPrelaunchFallback(modal);
      }
    }, 250);
  };

  const videoEmbedFrameSelector = [
    '.cg-ugc-card--has-embed .cg-ugc-card__embed iframe',
    '.cg-affiliate-signup-landing__video-card--has-embed .cg-affiliate-signup-landing__video-embed iframe',
    '.cg-product-videos--natural .cg-product-videos__embed iframe'
  ].join(',');

  const applyVideoEmbedSizing = (root = document) => {
    const frames = Array.from(root.querySelectorAll(videoEmbedFrameSelector));

    frames.forEach((frame) => {
      const height = Number.parseInt(frame.getAttribute('height') || '', 10);
      const embed = frame.closest(
        '.cg-ugc-card__embed, .cg-affiliate-signup-landing__video-embed, .cg-product-videos__embed'
      );

      frame.style.setProperty('aspect-ratio', 'auto', 'important');
      frame.style.setProperty('max-height', 'none', 'important');

      if (Number.isFinite(height) && height > 0) {
        frame.style.setProperty('height', `${height}px`, 'important');
        frame.style.setProperty('min-height', `${height}px`, 'important');
        embed?.style.setProperty('min-height', `${height}px`);
      }
    });
  };

  const initVideoEmbedSizing = (root = document) => {
    applyVideoEmbedSizing(root);
    window.setTimeout(() => applyVideoEmbedSizing(root), 500);
    window.setTimeout(() => applyVideoEmbedSizing(root), 1500);
    window.setTimeout(() => applyVideoEmbedSizing(root), 3000);
    window.setTimeout(() => applyVideoEmbedSizing(root), 6000);
    window.setTimeout(() => applyVideoEmbedSizing(root), 9000);

    if (root !== document || document.documentElement.dataset.cgVideoEmbedSizingInitialized === 'true') {
      return;
    }

    document.documentElement.dataset.cgVideoEmbedSizingInitialized = 'true';

    let frame = null;
    const requestSizing = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = null;
        applyVideoEmbedSizing();
      });
    };

    if (document.body) {
      const observer = new MutationObserver(requestSizing);
      observer.observe(document.body, {
        attributeFilter: ['height', 'style'],
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    window.addEventListener('resize', requestSizing);
  };

  const revealApplicationSuccessFromUrl = () => {
    const successTargets = [
      {
        param: 'wholesale_success',
        errorParam: 'wholesale_error',
        selector: '[data-cg-wholesale-success]'
      },
      {
        param: 'affiliate_success',
        errorParam: 'affiliate_error',
        selector: '[data-cg-affiliate-success]'
      }
    ];

    let shouldCleanUrl = false;

    try {
      const url = new URL(window.location.href);

      successTargets.forEach(({ param, errorParam, selector }) => {
        const hasSuccess = url.searchParams.has(param);
        const hasError = url.searchParams.has(errorParam);

        if (hasSuccess) {
          const success = document.querySelector(selector);

          if (success) {
            success.hidden = false;
            success.removeAttribute('hidden');
            success.focus?.({ preventScroll: true });
            window.setTimeout(() => {
              success.scrollIntoView({ block: 'center' });
            }, 50);
          }
        }

        if (hasSuccess || hasError) {
          url.searchParams.delete(param);
          url.searchParams.delete(errorParam);
          shouldCleanUrl = true;
        }
      });

      if (shouldCleanUrl && window.history?.replaceState) {
        window.history.replaceState(
          window.history.state,
          '',
          `${url.pathname}${url.search}${url.hash}`
        );
      }
    } catch (error) {}
  };

  const init = () => {
    document.querySelectorAll('[data-cg-site-header]').forEach(initSiteHeader);
    document.querySelectorAll('[data-cg-product-main]').forEach(initProductMain);
    document.querySelectorAll('[data-cg-product-videos]').forEach(initProductVideos);
    document.querySelectorAll('[data-cg-product-related]').forEach(initProductRelated);
    document.querySelectorAll('[data-cg-prelaunch-modal]').forEach(initPrelaunchModal);
    initPrelaunchTriggers();
    initVideoEmbedSizing();
    openPrelaunchFromUrl();
    revealApplicationSuccessFromUrl();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSiteHeader(event.target.querySelector('[data-cg-site-header]'));
    initProductMain(event.target.querySelector('[data-cg-product-main]'));
    initProductVideos(event.target.querySelector('[data-cg-product-videos]'));
    initProductRelated(event.target.querySelector('[data-cg-product-related]'));
    initPrelaunchModal(event.target.querySelector('[data-cg-prelaunch-modal]'));
    initVideoEmbedSizing(event.target);
    openPrelaunchFromUrl();
    revealApplicationSuccessFromUrl();
  });

  window.addEventListener('hashchange', openPrelaunchFromUrl);
})();
